import { Navigate, Outlet } from '@tanstack/react-router'
import { SlidersHorizontal } from 'lucide-react'
import { type ReactNode } from 'react'

import { EmptyState, Page, PageHeader } from '@aeris/ui'

import { ConfigTabs } from '../components/config-tabs.tsx'
import { useAuth } from '../lib/auth-context.ts'
import { defaultTabOf } from '../lib/config-tabs.ts'
import { useWorkspace } from '../lib/workspace.ts'

/**
 * Configurações do atendimento: oito telas, uma linha de menu (F068).
 *
 * _"Essa porra tem q ficar numa página de configuração, aí fica isso, os
 * modelos, robô de atendimento, custo de atendimento. Temos que deixar melhor
 * nossa sidebar, tá com mt página desnecessária q poderia ser 1, tá estranho."_
 * — palavras do dono.
 *
 * ## Por que uma rota de LAYOUT, e não uma tela nova
 *
 * A tentação é óbvia: um módulo `atendimento-config` com oito painéis dentro, e
 * as oito rotas antigas apagadas. Custaria caro e em silêncio.
 *
 * `PAGES` (`packages/permissions/src/pages.ts`) alimenta
 * `effectivePermissions()`: **cada tela é revogável separadamente** pelo admin,
 * e uma permissão só se perde quando a última tela que a exige é tirada.
 * Fundir os oito módulos colapsaria sete permissões numa e tiraria do
 * administrador a capacidade de negar "Custos de atendimento" sem negar o robô
 * junto. `page-access.test.ts` ainda exige `moduleForPath('/${module}') ===
 * module` para toda entrada de `PAGES`, então mover as URLs quebraria o guarda
 * que existe para que uma tela negada não entre pela barra de endereço.
 *
 * Então esta é uma rota **sem `path`** — o mesmo recurso que `shellRoute` já
 * usa. As oito telas continuam nos endereços que sempre tiveram, e ganham só
 * uma moldura em volta. O que sobrevive de graça: favoritos, o botão de voltar,
 * os dez links do guia de configuração, as janelas do Modo PC, `moduleForPath`,
 * o `PageGate`, `PAGES` e `effectivePermissions`.
 *
 * O que muda é a barra lateral, e só ela: `inTab` em `lib/modules.tsx` tira as
 * oito do menu — e as **mantém** no Ctrl+K e na grade do Início, porque uma
 * tela que some do menu e da busca ao mesmo tempo deixou de existir.
 *
 * ## O que ficou de fora
 *
 * O inbox e os chamados: são trabalho de todo dia, não configuração. E os
 * fluxos de página cheia — `/atendimento-modelos/novo`, `/crm-campanhas/nova`,
 * `/crm-regras/$id` —, porque uma trilha de abas em volta de um assistente é um
 * convite a abandoná-lo no meio. Eles já têm o link de volta no `eyebrow`.
 */
export const AtendimentoConfigLayout = (): ReactNode => (
  <Page>
    {/* Sem descrição (F132), como Configurações da loja: a aba se explica. */}
    <PageHeader title="Configurações do atendimento" />
    <ConfigTabs hub="atendimento-config" />
    <Outlet />
  </Page>
)

/**
 * A linha do menu, que não desenha nada.
 *
 * Ela existe porque a barra lateral precisa de um destino, e o destino certo
 * depende de quem está olhando: apontar direto para `/atendimento-guia` levaria
 * ao `PageGate` alguém que tem `communication.cost.read` e não tem
 * `communication.channel.read`.
 *
 * `replace` para que o botão de voltar não caia de novo aqui e ricocheteie.
 */
export const AtendimentoConfigHub = (): ReactNode => {
  const { allows, showsPage } = useAuth()
  const workspace = useWorkspace()

  const primeira = defaultTabOf(
    'atendimento-config',
    (entry) =>
      (entry.permission === undefined || allows(entry.permission)) &&
      showsPage(entry.module)
  )

  /*
   * Nenhuma aba visível não deveria acontecer — `anyOf` já tira o item do menu
   * de quem não pode abrir nenhuma —, mas ainda dá para chegar aqui pela URL,
   * e um redirecionamento para lugar nenhum seria uma tela branca.
   */
  if (primeira === undefined) {
    return (
      <Page>
        <PageHeader title="Configurações do atendimento" />
        <EmptyState
          icon={<SlidersHorizontal aria-hidden="true" className="h-8 w-8" />}
          title="Nada para configurar aqui"
          description="Seu acesso não inclui nenhuma das telas de configuração do atendimento. Fale com quem administra a conta."
        />
      </Page>
    )
  }

  return <Navigate replace to={workspace.routeFor(primeira.module)} />
}
